/**
 * React Query persistence for the field PWA.
 *
 * Why we need this:
 *   When a worker drops out of signal mid-task, we want previously-loaded
 *   inventory items / lots / areas to still render from cache. React Query's
 *   in-memory cache doesn't survive a browser refresh — `persistQueryClient`
 *   serializes it into IndexedDB so reads work across reloads + offline.
 *
 * Scope:
 *   We only persist field-related queries. The desktop app has lots of large,
 *   short-lived list queries that would bloat the persisted cache and make
 *   restarts slow. We use the `dehydrateOptions.shouldDehydrateQuery` filter
 *   to only persist queries whose key starts with one of our field prefixes.
 *
 * Storage:
 *   IndexedDB-backed sync persister via @tanstack/query-async-storage-persister
 *   would be ideal (large payloads), but to keep dependencies minimal we use
 *   the sync localStorage persister with a 5MB ceiling. If we hit the limit,
 *   we'll swap to async/IndexedDB.
 */

import { persistQueryClient } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import type { QueryClient } from '@tanstack/react-query'

const STORAGE_KEY = 'lithos-field-rq-cache-v1'

/** Query key prefixes that the field PWA cares about persisting. */
const FIELD_QUERY_PREFIXES = [
  'inventory_items',
  'inventory_locations',
  'lots',
  'areas',
  'jobs',
  'activities',
  'purchase_orders',
  'purchase_order_line_items',
  'materials',
  'vendors',
]

function isFieldQueryKey(key: readonly unknown[]): boolean {
  if (key.length === 0) return false
  const first = key[0]
  return typeof first === 'string' && FIELD_QUERY_PREFIXES.includes(first)
}

/**
 * Whether the current document is running inside the field PWA scope.
 *
 * The field PWA uses its own manifest (public/manifest.field.json:
 * `start_url` is `/field`), so an installed field PWA always BOOTS at a
 * `/field/*` path. The desktop office app (public/manifest.json, start_url:/)
 * boots at a non-`/field` path. We gate persistence on the boot pathname so
 * the field offline cache is NEVER restored into the desktop app.
 *
 * Why this matters (card rKkWHT_XhbXU): FIELD_QUERY_PREFIXES includes
 * 'activities', and `activitiesKeys.all = ['activities']`, so without this gate
 * EVERY activities query — including the desktop office lot page — got
 * dehydrated and restored on reload, painting a stale pre-completion snapshot.
 * Gating here keeps offline 'My Activities' working for field workers (the
 * whole point of the cache) while ensuring the desktop never serves reads from
 * the field-PWA offline cache.
 */
export function isFieldAppContext(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.pathname.startsWith('/field')
}

/** Wire up persistence for the given QueryClient. Idempotent. Field-app only. */
export function attachFieldQueryPersistence(queryClient: QueryClient): void {
  if (typeof window === 'undefined') return
  // Only the field PWA persists/restores reads. The desktop office app must
  // NOT restore field offline snapshots (see isFieldAppContext).
  if (!isFieldAppContext()) return

  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: STORAGE_KEY,
    // Keep payload small enough to avoid quota errors. ~3MB headroom under the
    // typical 5MB localStorage cap.
    serialize: JSON.stringify,
    deserialize: JSON.parse,
  })

  persistQueryClient({
    queryClient,
    persister,
    // Bust the cache on app version change (env var injected at build time).
    buster: (import.meta.env.VITE_BUILD_VERSION as string | undefined) ?? 'dev',
    // Drop persisted entries older than 7 days.
    maxAge: 7 * 24 * 60 * 60 * 1000,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        if (query.state.status !== 'success') return false
        return isFieldQueryKey(query.queryKey)
      },
    },
  })
}
